<template>
  <!-- Só para administradores: escolher as máquinas da execução ou comparar a latência
       da mesma simulação em várias configurações. O servidor confere o login. -->
  <v-card-text class="admin-run pt-2">
    <div class="d-flex align-center mb-2">
      <v-icon small color="primary" class="mr-1">mdi-shield-account</v-icon>
      <span class="text-subtitle-2">Cluster test (admin)</span>
    </div>
    <v-radio-group v-model="mode" dense hide-details class="mt-0" row>
      <v-radio value="auto" label="Automatic (Slurm decides)" />
      <v-radio value="manual" label="Choose machines" />
      <v-radio value="compare" label="Compare latency" />
    </v-radio-group>

    <template v-if="mode !== 'auto'">
      <v-alert v-if="loadError" dense text type="warning" class="mt-3 mb-0">{{ loadError }}</v-alert>
      <p class="text-caption mt-3 mb-1">
        {{ mode === "compare"
          ? "Machines to use, in order: the comparison runs with the first one, then the first two, and so on."
          : "Machines for this run:" }}
      </p>
      <div class="d-flex flex-wrap">
        <v-chip
          v-for="node in nodes"
          :key="node.name"
          small
          class="mr-2 mb-2"
          :disabled="!node.available"
          :color="selected.includes(node.name) ? 'primary' : undefined"
          :outlined="!selected.includes(node.name)"
          @click="toggle(node.name)"
        >
          <span v-if="selected.includes(node.name)" class="mr-1">{{ selected.indexOf(node.name) + 1 }}.</span>
          {{ node.name }}
          <span class="text-caption ml-1">({{ node.available ? `${coresOf(node)} cores` : "off" }})</span>
        </v-chip>
      </div>
      <v-row dense class="mt-1">
        <v-col cols="6">
          <v-text-field
            v-model.number="tasksPerNode"
            type="number"
            min="1"
            :max="maxCores"
            dense
            outlined
            hide-details
            :label="is3d ? 'MPI processes per machine' : 'Processes per machine'"
          />
        </v-col>
        <v-col v-if="is3d" cols="6">
          <v-text-field
            v-model.number="threads"
            type="number"
            min="1"
            :max="maxCores"
            dense
            outlined
            hide-details
            label="Threads per process"
          />
        </v-col>
      </v-row>
      <p v-if="selected.length" class="text-caption mt-1 mb-0" :class="overLimit ? 'error--text' : ''">
        Up to {{ maxCores }} {{ is3d ? "processes × threads" : "processes" }} per machine in this selection
        (one per physical core{{ reservedNote }}).<template v-if="overLimit"> Lower the value to run.</template>
      </p>
      <v-row v-if="mode === 'compare'" dense class="mt-1">
        <v-col cols="6">
          <v-text-field
            v-model.number="repetitions"
            type="number"
            min="1"
            max="10"
            dense
            outlined
            hide-details
            label="Repetitions of each configuration"
          />
        </v-col>
      </v-row>
      <v-checkbox
        v-if="mode === 'compare'"
        v-model="includeAuto"
        dense
        hide-details
        label="Also run with the automatic choice (baseline)"
      />
      <p class="text-caption mt-2 mb-0">
        <template v-if="mode === 'compare'">
          {{ configs.length }} run(s), one after the other. At the end the times are compared (queue, solver and
          click to result) and every output is checked against the first run.
        </template>
        <template v-else-if="selected.length">
          {{ selected.length }} machine(s) × {{ tasksPerNode }} process(es){{ is3d ? ` × ${threads} thread(s)` : "" }}.
        </template>
        <template v-else>Select at least one machine.</template>
      </p>
    </template>
  </v-card-text>
</template>

<script>
import cluster from "@/services/cluster";
import { adminRun } from "@/common/adminRun";

export default {
  name: "AdminRunPanel",
  props: {
    is3d: { type: Boolean, default: false }
  },
  data: () => ({
    mode: "auto",
    nodes: [],
    selected: [],
    tasksPerNode: 1,
    threads: 1,
    includeAuto: true,
    repetitions: 1,
    loadError: ""
  }),
  computed: {
    // Núcleos físicos utilizáveis: a máquina mais restrita da seleção limita todas
    maxCores() {
      const cores = this.nodes.filter(n => this.selected.includes(n.name)).map(this.coresOf);
      return cores.length ? Math.min(...cores) : 64;
    },
    overLimit() {
      return this.tasksPerNode * (this.is3d ? this.threads : 1) > this.maxCores;
    },
    // Máquinas com núcleos reservados ao sistema (ex.: o mestre, que também roda a interface e o NFS)
    reservedNote() {
      const reserved = this.nodes.filter(n => this.selected.includes(n.name) && n.cores && n.cores < Math.floor(n.cpus / 2));
      if (!reserved.length) return "";
      return `; ${reserved.map(n => `${n.name} keeps ${Math.floor(n.cpus / 2) - n.cores} cores for the system`).join(", ")}`;
    },
    // Configurações do comparativo: 1, 2, ..., N das máquinas escolhidas (na ordem do clique), cada uma
    // repetida `repetitions` vezes (média e desvio do tempo)
    configs() {
      const machines = this.selected.map((_, index) => ({
        label: `${index + 1} machine${index ? "s" : ""}`,
        nodes: this.selected.slice(0, index + 1),
        tasksPerNode: this.tasksPerNode,
        threads: this.is3d ? this.threads : undefined
      }));
      const base = this.includeAuto ? [{ label: "Automatic", nodes: null }, ...machines] : machines;
      const reps = Math.min(Math.max(Math.round(Number(this.repetitions) || 1), 1), 10);
      return base.flatMap(config => Array.from({ length: reps }, (_, rep) => ({ ...config, rep: rep + 1 })));
    }
  },
  watch: {
    mode: "sync",
    selected: "sync",
    tasksPerNode: "sync",
    threads: "sync",
    includeAuto: "sync",
    repetitions: "sync"
  },
  mounted() {
    this.loadNodes();
  },
  beforeDestroy() {
    // A escolha vale só enquanto a janela está aberta: a próxima execução volta ao automático
    adminRun.override = null;
    adminRun.overLimit = false;
    adminRun.mode = "auto";
  },
  methods: {
    async loadNodes() {
      try {
        const { data } = await cluster.get("");
        if (!data || !Array.isArray(data.nodes)) throw new Error("no cluster in this installation");
        this.nodes = data.nodes;
        // Padrão: um processo por núcleo físico (2 threads por núcleo nos nós do cluster)
        const first = data.nodes.find(n => n.available);
        if (first) {
          const cores = this.coresOf(first);
          this.tasksPerNode = this.is3d ? 1 : cores;
          this.threads = this.is3d ? cores : 1;
        }
      } catch (err) {
        this.loadError = `Could not list the machines: ${err.message}`;
      }
    },
    coresOf(node) {
      // Informado pelo serviço jobs (núcleos físicos menos os reservados); sem ele, metade das CPUs (2 threads por núcleo)
      return node.cores || Math.max(1, Math.floor(node.cpus / 2));
    },
    // Mantém processos × threads dentro dos núcleos da máquina mais restrita da seleção
    fitToSelection() {
      if (!this.selected.length) return;
      if (this.is3d) {
        this.tasksPerNode = Math.min(Math.max(this.tasksPerNode, 1), this.maxCores);
        if (this.tasksPerNode * this.threads > this.maxCores) this.threads = Math.max(1, Math.floor(this.maxCores / this.tasksPerNode));
      } else if (this.tasksPerNode > this.maxCores) {
        this.tasksPerNode = this.maxCores;
      }
    },
    toggle(name) {
      const index = this.selected.indexOf(name);
      if (index >= 0) this.selected.splice(index, 1);
      else this.selected.push(name);
      this.$nextTick(this.fitToSelection);
    },
    sync() {
      const manual = this.mode === "manual" && this.selected.length;
      adminRun.mode = this.mode;
      adminRun.overLimit = this.mode !== "auto" && this.selected.length > 0 && this.overLimit;
      adminRun.configs = this.mode === "compare" ? this.configs : [];
      adminRun.override = manual
        ? { nodes: [...this.selected], tasksPerNode: this.tasksPerNode, threads: this.is3d ? this.threads : undefined }
        : null;
    }
  }
};
</script>

<style scoped>
.admin-run {
  border-top: 1px dashed rgba(128, 128, 128, 0.4);
}
</style>
